import React, {
    Suspense
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { AppDispatch, RootState } from "../../../../globals";
import { setShowModal } from "../../../../redux/reducers/paymentsReducer";
import Constants, { FINANCES_MODALS, paymentStatus } from "../../../../constants";
import CancelInvoiceModalComponent from "../../../../components/modals/cancelInvoice/cancelInvoice";
import GenerateInvoiceModalComponent from "../../../../components/modals/generateInvoice/generateInvoice";
import InvoiceDetailModalComponent from "../../../../components/modals/invoiceDetails/invoiceDetails";
import CancelPaymentModalComponent from "../../../../components/modals/payments/cancel/cancelPayment";
import EmitPaymentModalComponent from "../../../../components/modals/payments/create/emitPayment";
import PaymentDetailModalComponent from "../../../../components/modals/payments/detail/paymentDetails";
import PaginationComponent from "../../../../components/pagination/pagination";
import Card from "../../../../components/card";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import PaymentUpdateModalComponent from "../../../../components/modals/payments/update/updatePayment";
import FiltrosPagosBarra, { FiltrosPagos, OpcionesPagos } from "./filtrosPagos";
//styles
import "./style.scss";
import "./pagos.css";
import "../finanzasLayout.css";

import AsideModalComponent from "../../../../components/modals/asideModal";
import { PERMISSIONS_CODES } from "../../../../constants";
import RegisterPaymentModal from "../../../../components/modals/payments/register/registerPaymentModal";
import RS from "../../../../components/modals/payments/register/strings";
import DiscountRequestsModal from "../../../../components/modals/payments/register/discountRequestsModal";
import ReceiptDetailModal from "../../../../components/modals/payments/multi/receiptDetailModal";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";
import { tituloEstado } from "../../../../utils/tituloEstado";
import useEsTelefono from "../../../../utils/esTelefono";

/*
    Pagos con la forma de Matrículas y Promociones (Esteban, 2026-11-04: "está horrible visualmente, los botones por todos lados,
    los filtros y el search bar no están de acorde a lo hablado, y hasta no cabe en la pantalla"):
    - título a la izquierda y TODOS los botones juntos a la derecha (Nuevo Pago es el principal);
    - una tarjeta con la fecha + buscador en una fila, las pastillas de filtro debajo, los totales y la tabla;
    - la tabla son 7 columnas que caben a 1280 px con el menú abierto; el clic en la fila abre el pago.
*/

export interface TotalesPagos { approved: number; collected: number; cancelled: number }

const gris: React.CSSProperties = { fontSize: 13, color: "var(--wa-apagado)" };
const Sub: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => <div className="wpgSub" style={gris} title={title}>{children}</div>;

export const dinero = (sym: string, n: number | string | null | undefined) =>
    (sym || "") + Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const nombreCompleto = (r: any) => [r.studentFirstname, r.studentLastname].map((x: any) => String(x || "").trim()).filter(Boolean).join(" ");
const nombreCorto = (r: any) => [String(r.studentFirstname || "").trim().split(/\s+/)[0], String(r.studentLastname || "").trim().split(/\s+/)[0]].filter(Boolean).join(" ");
// "Terminal POS" → "Terminal", "Transferencia" → "Transfer": el nombre completo queda en el título.
const metodoCorto = (m: string) => String(m || "").replace(/^Terminal POS$/i, "Terminal").replace(/^Transferencia$/i, "Transfer");
const conceptoCorto = (c: string) => tituloEstado(String(c || "").replace(/Cuota de inscripci[oó]n/gi, "Inscripción").replace(/Cuota de anulaci[oó]n/gi, "Anulación"));
const fechaDia = (s: string) => {
    const p = String(s || "").split("/");
    return p.length === 3 ? String(+p[0]).padStart(2, "0") + "/" + String(+p[1]).padStart(2, "0") + "/" + p[2] : (s || "-");
};
const hora = (s: string) => fechaHoraAmPm(s).split(" ").slice(1).join(" ");
const montoDe = (r: any) => r.isReceiptRow ? r.receiptMoney : dinero(r.currencyCode, r.amount?.value);

const Estado: React.FC<{ r: any }> = ({ r }) => {
    if (r.statusId === paymentStatus.APPROVED) return null;
    if (r.statusId === paymentStatus.CANCELLED) return <Badge bg="oro" style={{ fontWeight: 500 }}>Anulado</Badge>;
    return <Badge bg="secondary" style={{ fontWeight: 500 }}>{tituloEstado(r.status)}</Badge>;
};

export interface ListaPagosProps {
    filas: any[] | null;
    movil: boolean;
    bloqueado: boolean;
    cargando: boolean;
    onAbrir: (r: any) => void;
    onFactura: (invoiceId: number, studentId: number) => void;
}

const ListaPagos: React.FC<ListaPagosProps> = ({ filas, movil, bloqueado, cargando, onAbrir, onFactura }) => {
    // 13" al 125 % o con el menú lateral fijo: la Referencia pasa debajo de la cuenta y el alumno conserva su espacio.
    const angosto = useEsTelefono(1180);
    if (!filas) return <div className="loader" />;
    const tenue = { opacity: cargando ? 0.55 : 1 };
    const abrir = (r: any) => { if (!bloqueado) onAbrir(r); };
    const factura = (r: any) => r.invoiceId > 0 && !r.isReceiptRow ? (
        <button type="button" className="wpgEnlace" disabled={bloqueado} onClick={(e) => { e.stopPropagation(); onFactura(r.invoiceId, r.studentId); }}>
            Factura {r.invoiceId}
        </button>
    ) : null;
    const numero = (r: any) => r.isReceiptRow ? <b>Pago {r.id}</b>
        : r.isMulti ? <span style={{ whiteSpace: "nowrap" }}><span style={gris}>└&nbsp;</span>{r.movementLabel}</span>
        : <b>{r.id}</b>;
    const alumno = (r: any) => r.isReceiptRow
        ? <><b>{r.payerName || (r.personCount > 1 ? `${r.personCount} Personas` : "1 Persona")}</b><Sub>{r.payerName && r.personCount > 1 ? `${r.personCount} personas · ` : ""}{r.movementCount} movimientos</Sub></>
        : <><b className="wpgNombreLargo">{nombreCompleto(r) || "-"}</b><b className="wpgNombreCorto">{nombreCorto(r) || "-"}</b><Sub>{r.studentIdentityCode || ""}</Sub></>;
    const concepto = (r: any) => r.isReceiptRow ? null
        : <>{conceptoCorto(r.concept) || "-"}{r.notes ? <Sub title={r.notes}>{r.notes}</Sub> : null}</>;
    const metodo = (r: any) => <><span title={r.paymentMethod}>{metodoCorto(r.paymentMethod) || "-"}</span><Sub title={r.recipientAccount}>{r.recipientAccount || ""}</Sub>
        {angosto && r.reference ? <Sub title={r.reference}>Ref. {r.reference}</Sub> : null}</>;
    const fecha = (r: any) => {
        const registro = r.createdAtDateTime || r.createdAt || "";
        const dia = fechaDia(r.createdAt);
        const pagado = fechaDia(r.paymentDate);
        return <>{dia}<Sub title={r.paymentDate ? `Fecha de pago ${pagado}` : undefined}>{pagado !== dia && r.paymentDate ? `Pagado ${pagado.slice(0, 5)}` : hora(registro)}</Sub></>;
    };
    const vacio = filas.length < 1;

    if (movil) {
        if (vacio) return <div style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>Sin pagos con estos filtros.</div>;
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
                {filas.map((r: any) => (
                    <div key={(r.isReceiptRow ? "r" : "p") + r.id} className={"wpgTarjeta" + (r.isMulti && !r.isReceiptRow ? " wpgHija" : "") + (r.statusId === paymentStatus.CANCELLED ? " wpgAnulado" : "")}
                        style={tenue} role="button" tabIndex={0} onClick={() => abrir(r)} onKeyDown={(e) => { if (e.key === "Enter") abrir(r); }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                            <div style={{ minWidth: 0 }}>{alumno(r)}</div>
                            <div style={{ flexShrink: 0, textAlign: "right" }}><b className="wpgMonto">{montoDe(r)}</b><div><Estado r={r} /></div></div>
                        </div>
                        <div style={{ fontSize: 14, marginTop: 4 }}>
                            {r.isReceiptRow ? null : <div>{conceptoCorto(r.concept) || "-"}</div>}
                            <Sub>{numero(r)} · {metodoCorto(r.paymentMethod)}{r.reference ? " · " + r.reference : ""} · {fechaDia(r.createdAt)}</Sub>
                            {r.notes ? <Sub>{r.notes}</Sub> : null}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <table className="table table-hover wpgLista">
            <colgroup>
                <col style={{ width: angosto ? 122 : 132 }} />
                <col />
                <col style={{ width: angosto ? "24%" : "22%" }} />
                <col style={{ width: angosto ? 140 : 150 }} />
                {angosto ? null : <col style={{ width: 120 }} />}
                <col style={{ width: 108 }} />
                <col style={{ width: 120 }} />
            </colgroup>
            <thead>
                <tr>
                    <th>Pago</th>
                    <th>{Constants.Common.colAlumno}</th>
                    <th>Concepto</th>
                    <th>{Constants.Common.colMetodo}</th>
                    {angosto ? null : <th>{Constants.Common.reference}</th>}
                    <th>Fecha</th>
                    <th className="wpgNum">Monto</th>
                </tr>
            </thead>
            <tbody>
                {vacio ? <tr><td colSpan={angosto ? 6 : 7} style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>Sin pagos con estos filtros.</td></tr>
                    : filas.map((r: any) => (
                        <tr key={(r.isReceiptRow ? "r" : "p") + r.id}
                            className={(r.isReceiptRow ? "wpgFilaPago" : "") + (r.isMulti && !r.isReceiptRow ? " wpgHija" : "") + (r.statusId === paymentStatus.CANCELLED ? " wpgAnulado" : "")}
                            style={{ cursor: bloqueado ? "default" : "pointer", ...tenue }} onClick={() => abrir(r)}>
                            <td>{numero(r)}{factura(r) ? <div>{factura(r)}</div> : null}</td>
                            <td>{alumno(r)}</td>
                            <td>{concepto(r)}</td>
                            <td>{metodo(r)}</td>
                            {angosto ? null : <td className="wpgRef" title={r.reference || ""}>{r.reference || "-"}</td>}
                            <td>{fecha(r)}</td>
                            <td className="wpgNum"><span className="wpgMonto">{montoDe(r)}</span><div><Estado r={r} /></div></td>
                        </tr>
                    ))}
            </tbody>
        </table>
    );
};

const PaymentView = (props: any) => {
    const {
        listProps,
        paginationProps,
        searchProps,
        invoiceDetailProps,
        paymentDetailProps,
        emitPaymentProps,
        cancelPaymentProps,
        generateInvoiceProps,
        cancelInvoiceProps,
        paymentUpdateProps,
        multiProps,
        receiptProps
    } = props

    const dispatch = useDispatch<AppDispatch>();
    const permisos: any[] = useSelector((state: RootState) => state.rootReducer.sessionState.permissions) || [];
    const showModal: boolean = useSelector((state: RootState) => state.rootReducer.payments.showModal);
    const modalContent: any = useSelector((state: RootState) => state.rootReducer.payments.modalContent);
    const student: any = useSelector((state: RootState) => state.rootReducer.payments.studentData);
    const payment: any = useSelector((state: RootState) => state.rootReducer.payments.selectedPayment);
    const invoice: any = useSelector((state: RootState) => state.rootReducer.payments.selectedInvoice);
    const itemsInInvoice: any = useSelector((state: RootState) => state.rootReducer.payments.invoiceItems);
    const totalToPaid: any = useSelector((state: RootState) => state.rootReducer.payments.totalToPaid);

    const movil: boolean = listProps.movil;
    const totales: TotalesPagos | null = listProps.totales;
    const filtros: FiltrosPagos = listProps.filtros;
    const opciones: OpcionesPagos = listProps.opciones;

    const renderModalContent = (children: number) => {
        switch (children) {
            case FINANCES_MODALS.INVOICE:
                return (
                    <InvoiceDetailModalComponent
                        student={student}
                        invoice={invoice}
                        closeModal={invoiceDetailProps.closeModal}
                        showPaymentDetail={invoiceDetailProps.showPaymentDetail}
                        showCreditDetail={invoiceDetailProps.showCreditDetail}
                        cancelInvoice={invoiceDetailProps.cancelInvoice}
                        proccessPayment={invoiceDetailProps.proccessPayment}
                    />
                );
            case FINANCES_MODALS.GENERATE_INVOICE:
                return (
                    <GenerateInvoiceModalComponent
                        onSuccess={generateInvoiceProps.onSuccess}
                        closeModal={generateInvoiceProps.closeModal}
                    />
                );
            case FINANCES_MODALS.CANCEL_INVOICE:
                return (
                    <CancelInvoiceModalComponent
                        student={student}
                        invoice={invoice}
                        closeModal={cancelInvoiceProps.closeModal}
                        onSuccess={cancelInvoiceProps.onSuccess}
                    />
                );
            case FINANCES_MODALS.PAYMENT_DETAIL:
                return (
                    <PaymentDetailModalComponent
                        student={student}
                        payment={payment}
                        closeModal={paymentDetailProps.closeModal}
                        handleCancelPayment={paymentDetailProps.handleCancelPayment}
                        showPaymentUpdate={paymentDetailProps.showPaymentUpdate}
                    />
                );
            case FINANCES_MODALS.EMIT_PAYMENT:
                return (
                    <EmitPaymentModalComponent
                        student={student}
                        invoice={invoice}
                        itemsInInvoice={itemsInInvoice}
                        totalToPaid={totalToPaid}
                        isPartialPayment={emitPaymentProps.isPartialPayment}
                        isNewInvoice={emitPaymentProps.isNewInvoice}
                        closeModal={emitPaymentProps.closeModal}
                        cancelEmitPayment={emitPaymentProps.cancelEmitPayment}
                        onSuccess={emitPaymentProps.onSuccess}
                        showApplyCreditModal={emitPaymentProps.showApplyCreditModal}
                    />
                );
            case FINANCES_MODALS.CANCEL_PAYMENT:
                return (
                    <CancelPaymentModalComponent
                        student={student}
                        payment={payment}
                        closeModal={cancelPaymentProps.closeModal}
                        onSuccess={cancelPaymentProps.onSuccess}
                    />
                );
            case FINANCES_MODALS.UPDATE_PAYMENT:
                return (
                    <PaymentUpdateModalComponent
                        student={student}
                        payment={payment}
                        closeModal={paymentUpdateProps.closeModal}
                        onSuccess={paymentUpdateProps.onSuccess}
                    />
                );
            default:
                dispatch(setShowModal(false));
                return null;
        }
    }

    const puedeCrear = multiProps && permisos.includes(PERMISSIONS_CODES.PAYMENT_PERMISSIONS.CREATE);

    return (
        <div className={`transition-width${showModal ? " waFinanzasConPanel" : ""}`}>
            <div className="container-fluid waModulo wpgModulo" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
                <div className="wpgCabecera">
                    <h4 style={{ margin: 0 }}>{Constants.Common.payments}</h4>
                    <div className="wpgAcciones">
                        {/* Un solo botón naranja (como Nueva en Matrículas); lo demás en neutro. */}
                        {multiProps ? (
                            <button type="button" className="btn btn-light waBotonGrande" onClick={multiProps.discounts.show}>
                                {movil ? "Descuentos" : "Descuentos Especiales"}
                            </button>
                        ) : null}
                        {puedeCrear ? (
                            <button type="button" className="btn btn-primary waBotonGrande" onClick={multiProps.open}>
                                <FontAwesomeIcon icon={solid("plus")} />&nbsp;{RS.newPayment}
                            </button>
                        ) : null}
                    </div>
                </div>
                {multiProps?.avisoEnlace?.texto ? (
                    <div className="alert alert-warning" role="alert" style={{ fontSize: 15, cursor: "pointer" }} onClick={multiProps.avisoEnlace.cerrar}>{multiProps.avisoEnlace.texto}</div>
                ) : null}
                {listProps.enlazado ? (
                    <div className="alert alert-light wpgAvisoEnlace" role="alert">
                        <span>Pago {listProps.enlazado}</span>
                        <button type="button" className="btn btn-link btn-sm" onClick={listProps.quitarEnlace}>Ver Todos</button>
                    </div>
                ) : null}

                <Card>
                    <div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
                        <FiltrosPagosBarra valor={filtros} opciones={opciones} onChange={listProps.onFiltros} movil={movil}
                            accion={
                                // Descarga exactamente lo filtrado: por eso vive en la barra de filtros y no arriba.
                                <button type="button" className="btn btn-light rounded-pill wpgReporte" onClick={listProps.onReporte}>
                                    <FontAwesomeIcon icon={solid("file-excel")} />&nbsp;Descargar Reporte
                                </button>
                            }
                            buscador={<SearchInputComponent
                                search={searchProps.search}
                                onChangeSearch={searchProps.onChangeSearch}
                                onClickSearch={searchProps.onClickSearch}
                                onClearSearch={searchProps.onClearSearch}
                            />} />

                        {totales ? (
                            <div className="wpgKpis">
                                <div><small>Pagos</small><b>{totales.approved.toLocaleString("es-MX")}</b></div>
                                <div><small>Cobrado</small><b>{dinero(listProps.sym, totales.collected)}</b></div>
                                <div className={totales.cancelled ? "wpgKpiOro" : ""}><small>Anulados</small><b>{totales.cancelled.toLocaleString("es-MX")}</b></div>
                            </div>
                        ) : null}

                        <div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>
                            <ListaPagos filas={listProps.filas} movil={movil} bloqueado={listProps.bloqueado} cargando={listProps.cargando}
                                onAbrir={listProps.onAbrir} onFactura={listProps.onFactura} />
                        </div>
                    </div>
                </Card>
                <div className="waSobreDock" style={{ marginTop: 10 }}>
                    <PaginationComponent
                        pages={paginationProps.pages}
                        onPageSizeChange={paginationProps.onPageSize}
                        pageSizeVisible={true}
                        pageSizes={paginationProps.pageSizes}
                        itemCount={paginationProps.itemCount}
                    />
                </div>
            </div>
            <Suspense fallback={<div>Loading...</div>}>
                <AsideModalComponent
                    showModal={showModal}
                    location="right"
                    style={{ width: "648px" }}>
                    {renderModalContent(modalContent)}
                </AsideModalComponent>
            </Suspense>
            {multiProps && multiProps.discounts.open ? (
                <DiscountRequestsModal campusId={multiProps.campusId} currencySymbol={multiProps.currencySymbol} focusId={multiProps.discounts.focusId} onClose={multiProps.discounts.close} />
            ) : ""}
            {multiProps && multiProps.show ? (
                <Suspense fallback={<React.Fragment />}>
                    <RegisterPaymentModal campusId={multiProps.campusId} currencySymbol={multiProps.currencySymbol}
                        onClose={multiProps.close} onSuccess={multiProps.onSuccess} />
                </Suspense>
            ) : ""}
            {receiptProps && receiptProps.id ? (
                <Suspense fallback={<React.Fragment />}>
                    <ReceiptDetailModal campusId={receiptProps.campusId} receiptId={receiptProps.id} onClose={receiptProps.close}
                        onCancelled={receiptProps.onCancelled} onOpenMovement={receiptProps.onOpenMovement} />
                </Suspense>
            ) : ""}
        </div>
    )
}

export default PaymentView;
